import { supabase } from '../lib/supabase';

import { validationUtils } from '../utils/validation';

/**
 * Check if error is a schema-related error that should be thrown
 */
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const faqService = {
  /**
   * Get all FAQs for an institute
   */
  async getInstituteFAQs(instituteId, options = {}) {
    try {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }

      let query = supabase
        ?.from('faqs')
        ?.select('*')
        ?.eq('institute_id', instituteId);

      // Filter by active status if specified
      if (options?.activeOnly) {
        query = query?.eq('is_active', true);
      }

      // Filter by category if specified
      if (options?.category) {
        query = query?.eq('category', options?.category);
      }

      // Order by display_order and created_at
      query = query?.order('display_order', { ascending: true })
        ?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getInstituteFAQs:', error?.message);
          throw error;
        }
        throw new Error(error?.message || 'Failed to fetch FAQs');
      }

      return data?.map(faq => ({
        id: faq?.id,
        instituteId: faq?.institute_id,
        question: faq?.question,
        answer: faq?.answer,
        category: faq?.category,
        displayOrder: faq?.display_order,
        isActive: faq?.is_active,
        createdAt: faq?.created_at,
        updatedAt: faq?.updated_at
      })) || [];
    } catch (error) {
      if (isSchemaError(error)) {
        throw error;
      }
      console.error('Error in getInstituteFAQs:', error);
      return [];
    }
  },

  /**
   * Get FAQ by ID
   */
  async getFAQById(faqId) {
    try {
      if (!validationUtils?.isValidUUID(faqId)) {
        throw new Error('Invalid FAQ ID');
      }

      const { data, error } = await supabase
        ?.from('faqs')
        ?.select('*')
        ?.eq('id', faqId)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          throw error;
        }
        throw new Error(error?.message || 'Failed to fetch FAQ');
      }

      return {
        id: data?.id,
        instituteId: data?.institute_id,
        question: data?.question,
        answer: data?.answer,
        category: data?.category,
        displayOrder: data?.display_order,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error in getFAQById:', error);
      throw error;
    }
  },

  /**
   * Create a new FAQ
   */
  async createFAQ(faqData) {
    try {
      // Validate required fields
      if (!faqData?.question?.trim()) {
        throw new Error('Question is required');
      }
      if (!faqData?.answer?.trim()) {
        throw new Error('Answer is required');
      }
      if (!faqData?.category) {
        throw new Error('Category is required');
      }
      if (!faqData?.instituteId) {
        throw new Error('Institute ID is required');
      }

      const { data, error } = await supabase
        ?.from('faqs')
        ?.insert({
          institute_id: faqData?.instituteId,
          question: faqData?.question?.trim(),
          answer: faqData?.answer?.trim(),
          category: faqData?.category,
          display_order: faqData?.displayOrder || 0,
          is_active: faqData?.isActive !== false
        })
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          throw error;
        }
        throw new Error(error?.message || 'Failed to create FAQ');
      }

      return {
        id: data?.id,
        instituteId: data?.institute_id,
        question: data?.question,
        answer: data?.answer,
        category: data?.category,
        displayOrder: data?.display_order,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error in createFAQ:', error);
      throw error;
    }
  },

  /**
   * Update an existing FAQ
   */
  async updateFAQ(faqId, updates) {
    try {
      if (!validationUtils?.isValidUUID(faqId)) {
        throw new Error('Invalid FAQ ID');
      }

      const updateData = {};
      
      if (updates?.question !== undefined) {
        if (!updates?.question?.trim()) {
          throw new Error('Question cannot be empty');
        }
        updateData.question = updates?.question?.trim();
      }
      
      if (updates?.answer !== undefined) {
        if (!updates?.answer?.trim()) {
          throw new Error('Answer cannot be empty');
        }
        updateData.answer = updates?.answer?.trim();
      }
      
      if (updates?.category !== undefined) {
        updateData.category = updates?.category;
      }
      
      if (updates?.displayOrder !== undefined) {
        updateData.display_order = updates?.displayOrder;
      }
      
      if (updates?.isActive !== undefined) {
        updateData.is_active = updates?.isActive;
      }

      const { data, error } = await supabase
        ?.from('faqs')
        ?.update(updateData)
        ?.eq('id', faqId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          throw error;
        }
        throw new Error(error?.message || 'Failed to update FAQ');
      }

      return {
        id: data?.id,
        instituteId: data?.institute_id,
        question: data?.question,
        answer: data?.answer,
        category: data?.category,
        displayOrder: data?.display_order,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error in updateFAQ:', error);
      throw error;
    }
  },

  /**
   * Delete a FAQ
   */
  async deleteFAQ(faqId) {
    try {
      if (!validationUtils?.isValidUUID(faqId)) {
        throw new Error('Invalid FAQ ID');
      }

      const { error } = await supabase
        ?.from('faqs')
        ?.delete()
        ?.eq('id', faqId);

      if (error) {
        if (isSchemaError(error)) {
          throw error;
        }
        throw new Error(error?.message || 'Failed to delete FAQ');
      }

      return true;
    } catch (error) {
      console.error('Error in deleteFAQ:', error);
      throw error;
    }
  },

  /**
   * Search FAQs by query string
   */
  async searchFAQs(instituteId, searchQuery) {
    try {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }

      if (!searchQuery?.trim()) {
        return this.getInstituteFAQs(instituteId, { activeOnly: true });
      }

      const sanitizedQuery = validationUtils?.sanitizeString(searchQuery);

      const { data, error } = await supabase
        ?.from('faqs')
        ?.select('*')
        ?.eq('institute_id', instituteId)
        ?.eq('is_active', true)
        ?.or(`question.ilike.%${sanitizedQuery}%,answer.ilike.%${sanitizedQuery}%`)
        ?.order('display_order', { ascending: true })
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          throw error;
        }
        throw new Error(error?.message || 'Failed to search FAQs');
      }

      return data?.map(faq => ({
        id: faq?.id,
        instituteId: faq?.institute_id,
        question: faq?.question,
        answer: faq?.answer,
        category: faq?.category,
        displayOrder: faq?.display_order,
        isActive: faq?.is_active,
        createdAt: faq?.created_at,
        updatedAt: faq?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error in searchFAQs:', error);
      return [];
    }
  },

  /**
   * Get FAQ categories
   */
  getFAQCategories() {
    return [
      { value: 'Admissions', label: 'Admissions' },
      { value: 'Courses', label: 'Courses' },
      { value: 'Fees', label: 'Fees' },
      { value: 'Campus Life', label: 'Campus Life' },
      { value: 'Placements', label: 'Placements' },
      { value: 'Scholarships', label: 'Scholarships' },
      { value: 'Facilities', label: 'Facilities' },
      { value: 'General', label: 'General' }
    ];
  }
};